// features/promos/offpeak.ts — luật thuần Giờ Vàng phía FE (mirror
// backend/internal/core/offpeak + nhánh cộng ưu đãi trong customerbooking's
// PublicCreate). BE là nguồn sự thật cuối: con số hiện ở đây chỉ là bản xem
// trước để trang không hiện một đằng rồi thu một nẻo. Sửa một bên phải sửa
// cả hai.

/** Bản sao offpeak.CodePrefix (Go). Mã đầy đủ: GIOVANG-<CHINHANH>-<YYYYMMDD>-<HHMM>. */
export const OFFPEAK_CODE_PREFIX = "GIOVANG-";

/**
 * Mã này có phải mã Giờ Vàng tự nhả không.
 *
 * So sau khi cắt khoảng trắng + viết HOA: người dùng gõ tay/dán lại thì
 * hoa-thường không còn chắc chắn.
 */
export function isOffpeakCode(code: string | null | undefined): boolean {
  return (code ?? "").trim().toUpperCase().startsWith(OFFPEAK_CODE_PREFIX);
}

/** Mã khách gõ (mã giảm tiền hoặc mã giới thiệu) sau khi hệ ưu đãi báo giá. */
export type ManualDiscount = { code: string; discount: number };

/**
 * Báo giá Giờ Vàng. `code` để tuỳ chọn cho khớp schema sinh từ OpenAPI —
 * server chỉ trả mã khi áp được. Không cần cờ `applicable`: mọi nhánh từ
 * chối của BE đều trả amount = 0.
 */
export type OffpeakDiscount = { code?: string | null; amount: number };

export type AddedDiscounts = {
  code: string;
  codeDiscount: number;
  offpeakCode: string;
  offpeakDiscount: number;
  /** Tổng hai khoản — đúng số máy chủ ghi vào promoDiscount của đơn. */
  discount: number;
};

/**
 * Cộng mã khách gõ với Giờ Vàng (QĐ 0511, CEO 1b).
 *
 * Không tự kẹp định mức ở đây: nơi gọi hỏi báo giá kèm `otherDiscount` nên số
 * Giờ Vàng máy chủ trả về đã nhường đủ phần của mã.
 */
export function addDiscounts(
  manual: ManualDiscount | null | undefined,
  offpeak: OffpeakDiscount | null | undefined,
): AddedDiscounts {
  const codeDiscount = manual && manual.discount > 0 ? manual.discount : 0;
  const offpeakDiscount = offpeak && offpeak.amount > 0 ? offpeak.amount : 0;
  return {
    code: codeDiscount > 0 ? (manual?.code ?? "") : "",
    codeDiscount,
    offpeakCode: offpeakDiscount > 0 ? (offpeak?.code ?? "") : "",
    offpeakDiscount,
    discount: codeDiscount + offpeakDiscount,
  };
}

/**
 * Phần của MÃ trong tổng giảm trên đơn. `promoDiscount` là tổng hai khoản nên
 * dòng nào gọi tên mã phải trừ phần Giờ Vàng ra, kẻo khách thấy mã giảm lố.
 */
export function orderCodeDiscount(o: {
  promoDiscount?: number | null;
  offpeakDiscount?: number | null;
}): number {
  return Math.max(0, (o.promoDiscount ?? 0) - (o.offpeakDiscount ?? 0));
}

/** Nhãn hiển thị cạnh giá. */
export function offpeakLabel(pct: number): string {
  return `⚡ Giờ Vàng −${pct}%`;
}

/**
 * Nắn giờ về đúng dạng `HH:MM` mà tuyến báo giá đòi — "" khi không đọc nổi.
 *
 * Hook `useOffpeakQuote` KHÔNG nắn hộ, mà hai đầu đều gãy câm: `"9:00"` sinh
 * mã khung thiếu chữ số (lệch với mã máy chủ tự sinh), còn `"09:00:00"` —
 * dạng kiểu TIME của Postgres trả về trên đơn cũ — bị máy chủ từ chối thẳng.
 * Trả "" để nơi gọi tự tắt câu hỏi, thay vì hỏi bằng một giờ bịa ra.
 */
export function normalizeHHMM(value: string | null | undefined): string {
  const m = /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/.exec((value ?? "").trim());
  if (!m) return "";
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return "";
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export type ParsedOffpeakCode = {
  /** Chữ THƯỜNG để tra thẳng vào danh mục chi nhánh (mã trong mã là chữ HOA). */
  branchCode: string;
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM. */
  startTime: string;
};

/**
 * Bóc `GIOVANG-<CHINHANH>-<YYYYMMDD>-<HHMM>` ra chi nhánh / ngày / giờ mở khung.
 *
 * Cắt từ ĐUÔI chứ không tách theo thứ tự từ đầu: mã chi nhánh do người dùng
 * đặt, có thể chứa dấu gạch ngang, còn hai đoạn cuối thì luôn là 8 rồi 4 chữ
 * số. Không khớp khuôn → `null` để nơi gọi in nguyên mã thay vì bịa ra khung.
 */
export function parseOffpeakCode(code: string | null | undefined): ParsedOffpeakCode | null {
  const raw = (code ?? "").trim();
  if (!isOffpeakCode(raw)) return null;
  const m = /^(.+)-(\d{8})-(\d{4})$/.exec(raw.slice(OFFPEAK_CODE_PREFIX.length));
  if (!m) return null;
  const [, branch, ymd, hhmm] = m as unknown as [string, string, string, string];
  const hour = Number(hhmm.slice(0, 2));
  const minute = Number(hhmm.slice(2));
  if (hour > 23 || minute > 59) return null;
  return {
    branchCode: branch.toLowerCase(),
    date: `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6)}`,
    startTime: `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`,
  };
}
